import { canUseDOM } from '@ps/web-core-utils';
import type { SchedulerLike } from 'rxjs';
import { Subject, interval, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import type { EffectParams, WidgetEffectParams } from '@swf/core/store';
import { authChange, pageChange, signUpLocationChange } from '@swf/core/utils/actions';

import { POLLING_INTERVAL } from './constants';

export interface ActionOptions {
  pollingInterval?: number;
  scheduler?: SchedulerLike;
}

const is4xxError = (statusCode: number) => statusCode >= 400 && statusCode < 500;

const requestError = (actions: EffectParams['actions'], actionType: string) =>
  canUseDOM
    ? actions.pipe(filter(({ type, payload }) => type === actionType && is4xxError(payload)))
    : null;

export type PollingSource = 'PAGE_CHANGE' | 'AUTH_CHANGE' | 'INTERVAL' | 'SIGN_UP';

/**
 * Sets up widget polling and returns observable containing whether the action
 * was triggered by an interval, page change and auth change.
 *
 * @param {WidgetEffectParams} params - The parameters for the widget effect.
 * @param {ActionOptions} options - The options for the action.
 * @returns {Observable<PollingSource>} - The observable that emits the polling source.
 */
export function sportsWidgetPollingBehavior(
  { actions, widget }: WidgetEffectParams,
  { pollingInterval = POLLING_INTERVAL, scheduler }: ActionOptions = {}
) {
  const subject = new Subject<PollingSource>();
  const pageChangeObservable = pageChange(actions);
  const authChangeObservable = authChange(actions);
  const signUpLocationChangeObservable = signUpLocationChange(actions);

  const widgetRequestError = requestError(actions, `${widget.uid}/dataLoadFailure`);

  const takeUntilOperator = takeUntil(
    widgetRequestError ? merge(widget.unmount, widgetRequestError) : widget.unmount
  );

  pageChangeObservable
    ?.pipe(
      startWith(0),
      switchMap(() => interval(pollingInterval, scheduler).pipe(takeUntilOperator)),
      takeUntil(widget.unmount)
    )
    .subscribe({
      next: () => {
        subject.next('INTERVAL');
      }
    });

  pageChangeObservable?.subscribe({
    next: () => {
      subject.next('PAGE_CHANGE');
    }
  });

  authChangeObservable?.pipe(takeUntil(widget.unmount)).subscribe({
    next: () => {
      subject.next('AUTH_CHANGE');
    }
  });

  signUpLocationChangeObservable?.subscribe({
    next: () => {
      subject.next('SIGN_UP');
    }
  });

  return subject.asObservable().pipe(takeUntil(widget.unmount));
}

export const sportsUrlParamChange = (params: WidgetEffectParams, urlParam: string) =>
  pageChange(params.actions)?.pipe(
    map(({ state }) => params.select.sports.getUrlParams(state)[urlParam]),
    distinctUntilChanged()
  );
